import { IconGraphy, Avatar, Tooltip } from '@leapeasy/ui-kit';
import styled from 'styled-components';
import { Box } from '@mui/material';
import { useState, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Components
import ProfileView from './ProfileView';
import { ResetPasswordModal } from './ResetPasswordModal';
import { LogoutModal } from './LogoutModal';
import { NotificationBell } from './NotificationBell';
import { DemoModeToggle } from './DemoModeToggle';
import { GuideSlider } from 'components/GuideSlider';
import { useDemoMask } from 'hooks/useDemoMask';
import { notificationWebSocket } from 'utils/notificationWebSocket';
import { roleLabelForDisplay } from 'utils/entityTerminology';

// Context
import { UserContext } from 'context/context';
import { Layerbar } from 'components/common/Layerbar';
import { ProfileEdit } from 'components/ProfileEdit';
import { clearPostLoginReturnPath } from 'utils/postLoginReturnPath';
import { DEFAULT_PROFILE_AVATAR } from 'data/images/avatars';
import { shouldShowTopbarGuideSlider } from 'utils/portalGuideUi';

const elem = document.documentElement;

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.mozFullScreenElement ||
  document.msFullscreenElement;

const isFullscreenActive = () => Boolean(getFullscreenElement());

export const Topbar = () => {
  const { isDemo, mask } = useDemoMask();
  const collapsed = useSelector((state) => state.getIn(['ui', 'collapsed']));
  const currentUserFromStore = useSelector((state) => state.getIn(['auth', 'currentUser']));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const showGuideSlider = shouldShowTopbarGuideSlider();

  const [fullScreen, setFullScreen] = useState(isFullscreenActive);
  const [profileOpen, setProfileOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const currentUserFromLocalStorage = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (error) {
      return null;
    }
  })();

  const currentUser = (() => {
    if (currentUserFromStore?.toJS) {
      return currentUserFromStore.toJS();
    }
    if (currentUserFromStore) {
      return currentUserFromStore;
    }
    return currentUserFromLocalStorage;
  })();

  useEffect(() => {
    const syncFullscreenState = () => {
      setFullScreen(isFullscreenActive());
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    document.addEventListener('mozfullscreenchange', syncFullscreenState);
    document.addEventListener('MSFullscreenChange', syncFullscreenState);

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
      document.removeEventListener('mozfullscreenchange', syncFullscreenState);
      document.removeEventListener('MSFullscreenChange', syncFullscreenState);
    };
  }, []);

  const openFullScreen = async () => {
    try {
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        await elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari & Opera */
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        await elem.msRequestFullscreen();
      }
    } catch {
      setFullScreen(false);
    }
  };

  const closeFullScreen = async () => {
    if (!isFullscreenActive()) {
      setFullScreen(false);
      return;
    }

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch {
      // Browser may reject exit when the document is inactive or already exited.
    } finally {
      setFullScreen(isFullscreenActive());
    }
  };

  const getJobTitle = (role) => roleLabelForDisplay(role) || role;

  const handleConfirmLogout = () => {
    notificationWebSocket.close();
    // Hard-navigate without dispatching logOut — avoids DashboardLayout re-stashing
    // the current URL into sessionStorage before the document unloads.
    clearPostLoginReturnPath();
    localStorage.clear();
    window.location.replace('/login');
  };

  return (
    <TopbarContainer $collapsed={collapsed}>
      {/** actions */}
      <Box display="flex">
        <IconContainer>
          <Tooltip
            placement="bottom"
            showCarot={false}
            title={fullScreen ? 'Exit Full Screen' : 'Full Screen'}
            variant="contained"
          >
            <div
              onClick={() => {
                if (isFullscreenActive()) {
                  closeFullScreen();
                } else {
                  openFullScreen();
                }
              }}
            >
              <IconGraphy
                icon={!fullScreen ? 'EditorLayout.Fullscreen' : 'EditorLayout.FullscreenExit'}
                width={16}
                height={16}
                forceColor={true}
              />
            </div>
          </Tooltip>

          <Tooltip
            placement="bottom"
            showCarot={false}
            title={showGuideSlider ? 'Show guide' : 'Open portal guide'}
            variant="contained"
          >
            <div
              onClick={() => {
                if (showGuideSlider) {
                  setGuideOpen(true);
                  return;
                }
                navigate('/guide');
              }}
            >
              <IconGraphy icon="General.QuestionMark" width={16} height={16} forceColor={true} />
            </div>
          </Tooltip>

          <DemoModeToggle />

          <MobileSidebarToggle>
            <Tooltip placement="bottom" showCarot={false} title="Show sidebar" variant="contained">
              <MobileSidebarToggleButton
                type="button"
                aria-label="Show sidebar"
                aria-expanded={Boolean(userContext.menuOpenInMobile)}
                aria-controls="mobile-navigation-menu"
                onClick={() => {
                  if (userContext.toggleMobileMenu) {
                    userContext.toggleMobileMenu();
                    return;
                  }
                  userContext.setMenuOpenInMobile?.(!userContext.menuOpenInMobile);
                }}
              >
                <IconGraphy icon="EditorLayout.MenuOpen" width={20} height={16} forceColor={true} />
              </MobileSidebarToggleButton>
            </Tooltip>
          </MobileSidebarToggle>
        </IconContainer>
      </Box>

      <ProfileSection>
        <NotificationBell />
        {currentUser && (
          <ProfileTrigger onClick={() => setProfileOpen(!profileOpen)}>
            <Box minWidth="120px">
              <Avatar
                size="large"
                title={mask('username', currentUser.name)}
                description={getJobTitle(currentUser.jobTitle || currentUser.role)}
                iconImage={
                  isDemo ? undefined : (
                    <img
                      src={currentUser.image || DEFAULT_PROFILE_AVATAR}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DEFAULT_PROFILE_AVATAR;
                      }}
                    />
                  )
                }
                state="online"
              />
            </Box>
            <ExpandIcon icon="Arrow.ExpandMore" width={10} height={6} />
          </ProfileTrigger>
        )}
        {profileOpen && (
          <ProfileView
            setInvisible={setProfileOpen}
            setEditDialogOpen={setEditDialogOpen}
            onRequestLogout={() => setLogoutModalOpen(true)}
            name={mask('username', currentUser.name)}
            title={getJobTitle(currentUser.jobTitle || currentUser.role)}
            email={mask('email', currentUser.email)}
            phone_number={mask('phone', currentUser.phone)}
          />
        )}

        {editDialogOpen && (
          <Layerbar>
            <ProfileEdit
              closeDialog={() => setEditDialogOpen(false)}
              onResetPassword={() => {
                setEditDialogOpen(false);
                setResetPasswordOpen(true);
              }}
              name={currentUser.name}
              phone_number={currentUser.phone}
              email={currentUser.email}
              image={currentUser.image}
              mfaMethod={currentUser.mfaMethod}
              jobTitle={currentUser.jobTitle || currentUser.role}
            />
          </Layerbar>
        )}

        <ResetPasswordModal
          open={resetPasswordOpen}
          onClose={() => setResetPasswordOpen(false)}
          defaultEmail={currentUser?.email || ''}
        />
        <LogoutModal
          open={logoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          onConfirm={handleConfirmLogout}
          userImage={currentUser?.image}
        />
      </ProfileSection>
      {showGuideSlider && <GuideSlider open={guideOpen} onClose={() => setGuideOpen(false)} />}
    </TopbarContainer>
  );
};

const TopbarContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 32px;
  background: white;
  padding-left: 32px;
  transition: all 0.5s ease;

  @media screen and (min-width: ${({ theme }) => theme['2xl']}) {
    padding-left: ${({ $collapsed }) => ($collapsed ? '64px' : '32px')};
  }
`;

const ProfileSection = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProfileTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const ExpandIcon = styled(IconGraphy)`
  color: #867b8d;
`;

const IconContainer = styled.div`
  color: #3c393d;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: center;
`;

const MobileSidebarToggle = styled.div`
  display: none;

  @media screen and (max-width: ${(props) => props.theme.mobileSidebarMax}) {
    display: block;
  }
`;

const MobileSidebarToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
